import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getConceptosPlanilla, createConceptoPlanilla } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileSpreadsheet, Plus, TrendingUp, TrendingDown, Building2, Check, X, Lock } from 'lucide-react';

const TIPOS_CONCEPTO = [
  { value: 'INGRESO', label: 'Ingreso', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'DESCUENTO', label: 'Descuento', color: 'bg-red-100 text-red-700' },
  { value: 'APORTE_EMPLEADOR', label: 'Aporte Empleador', color: 'bg-blue-100 text-blue-700' },
];

const SUBTIPOS = {
  INGRESO: [
    { value: 'REMUNERACION', label: 'Remuneración' },
    { value: 'HORAS_EXTRAS', label: 'Horas Extras' },
    { value: 'BONIFICACION', label: 'Bonificación' },
    { value: 'GRATIFICACION', label: 'Gratificación' },
    { value: 'VACACIONES', label: 'Vacaciones' },
    { value: 'CONDICION', label: 'Condición de Trabajo' },
    { value: 'OTRO', label: 'Otro' },
  ],
  DESCUENTO: [
    { value: 'PENSION', label: 'Pensión (AFP/ONP)' },
    { value: 'IMPUESTO', label: 'Impuesto' },
    { value: 'DESCUENTO', label: 'Descuento' },
    { value: 'ADELANTO', label: 'Adelanto' },
    { value: 'PRESTAMO', label: 'Préstamo' },
    { value: 'OTRO', label: 'Otro' },
  ],
  APORTE_EMPLEADOR: [
    { value: 'SALUD', label: 'Salud (EsSalud)' },
    { value: 'SCTR', label: 'SCTR' },
    { value: 'SEGURO', label: 'Seguro' },
    { value: 'OTRO', label: 'Otro' },
  ]
};

const INITIAL_FORM = {
  codigo: '',
  nombre: '',
  tipo: 'INGRESO',
  subtipo: '',
  afecto_renta: true,
  afecto_essalud: true,
  afecto_pension: true,
  formula: '',
  valor_fijo: '',
  porcentaje: '',
};

const ConceptosPlanillaPage = () => {
  const { empresaActual } = useApp();
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('all');
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getConceptosPlanilla(empresaActual?.id);
      setConceptos(res.data);
    } catch (error) {
      toast.error('Error al cargar conceptos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNew = () => {
    setFormData(INITIAL_FORM);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.codigo || !formData.nombre || !formData.tipo) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      await createConceptoPlanilla({
        ...formData,
        empresa_id: empresaActual?.id,
        valor_fijo: formData.valor_fijo ? parseFloat(formData.valor_fijo) : null,
        porcentaje: formData.porcentaje ? parseFloat(formData.porcentaje) : null,
        es_sistema: false
      });
      toast.success('Concepto creado');
      setModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Filtrar conceptos
  const conceptosFiltrados = conceptos.filter(c => 
    filtroTipo === 'all' || c.tipo === filtroTipo
  );

  // Agrupar por tipo
  const ingresos = conceptos.filter(c => c.tipo === 'INGRESO');
  const descuentos = conceptos.filter(c => c.tipo === 'DESCUENTO');
  const aportes = conceptos.filter(c => c.tipo === 'APORTE_EMPLEADOR');

  return (
    <div className="space-y-6" data-testid="conceptos-planilla-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-purple-600" />
            Conceptos de Planilla
          </h1>
          <p className="text-gray-500 text-sm mt-1">Ingresos, descuentos y aportes para cálculo de planilla</p>
        </div>
        <Button onClick={handleNew} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Concepto
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ingresos</p>
              <p className="text-2xl font-bold text-emerald-600">{ingresos.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Descuentos</p>
              <p className="text-2xl font-bold text-red-600">{descuentos.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Aportes Empleador</p>
              <p className="text-2xl font-bold text-blue-600">{aportes.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs por tipo */}
      <Tabs defaultValue="all" onValueChange={setFiltroTipo}>
        <TabsList>
          <TabsTrigger value="all">Todos ({conceptos.length})</TabsTrigger>
          <TabsTrigger value="INGRESO" className="text-emerald-600">Ingresos ({ingresos.length})</TabsTrigger>
          <TabsTrigger value="DESCUENTO" className="text-red-600">Descuentos ({descuentos.length})</TabsTrigger>
          <TabsTrigger value="APORTE_EMPLEADOR" className="text-blue-600">Aportes ({aportes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value={filtroTipo} className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando...</div>
              ) : conceptosFiltrados.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No hay conceptos en esta categoría</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Subtipo</TableHead>
                      <TableHead className="text-center">Afecto Renta</TableHead>
                      <TableHead className="text-center">Afecto EsSalud</TableHead>
                      <TableHead className="text-center">Afecto Pensión</TableHead>
                      <TableHead className="text-right">%</TableHead>
                      <TableHead className="text-center">Sistema</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conceptosFiltrados.map((c) => {
                      const tipoInfo = TIPOS_CONCEPTO.find(t => t.value === c.tipo);
                      return (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono text-sm font-medium">{c.codigo}</TableCell>
                          <TableCell className="font-medium">{c.nombre}</TableCell>
                          <TableCell>
                            <Badge className={tipoInfo?.color}>{tipoInfo?.label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{c.subtipo || '-'}</TableCell>
                          <TableCell className="text-center">
                            {c.afecto_renta ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <X className="w-4 h-4 text-gray-300 mx-auto" />}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.afecto_essalud ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <X className="w-4 h-4 text-gray-300 mx-auto" />}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.afecto_pension ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <X className="w-4 h-4 text-gray-300 mx-auto" />}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {c.porcentaje ? `${(c.porcentaje * 100).toFixed(2)}%` : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.es_sistema && <Lock className="w-4 h-4 text-gray-400 mx-auto" title="Concepto del sistema" />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Nuevo Concepto */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              Nuevo Concepto
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input 
                  value={formData.codigo} 
                  onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase()})}
                  placeholder="Ej: BONIF_PROD"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v, subtipo: ''})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONCEPTO.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Nombre *</Label>
              <Input 
                value={formData.nombre} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Bonificación por Producción"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Subtipo</Label>
              <Select value={formData.subtipo} onValueChange={(v) => setFormData({...formData, subtipo: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {(SUBTIPOS[formData.tipo] || []).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Porcentaje (si aplica)</Label>
              <Input 
                type="number"
                step="0.0001"
                value={formData.porcentaje} 
                onChange={(e) => setFormData({...formData, porcentaje: e.target.value})}
                placeholder="Ej: 0.25 para 25%"
                className="mt-1"
              />
            </div>

            <div className="space-y-3 pt-2 border-t">
              <p className="text-sm font-medium text-gray-700">Afectaciones</p>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="afecto_renta"
                  checked={formData.afecto_renta}
                  onCheckedChange={(v) => setFormData({...formData, afecto_renta: v})}
                />
                <Label htmlFor="afecto_renta">Afecto a Renta 5ta Categoría</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="afecto_essalud"
                  checked={formData.afecto_essalud}
                  onCheckedChange={(v) => setFormData({...formData, afecto_essalud: v})}
                />
                <Label htmlFor="afecto_essalud">Afecto a EsSalud (9%)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="afecto_pension"
                  checked={formData.afecto_pension}
                  onCheckedChange={(v) => setFormData({...formData, afecto_pension: v})}
                />
                <Label htmlFor="afecto_pension">Afecto a Pensión (AFP/ONP)</Label>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConceptosPlanillaPage;
